import { z } from 'zod';
import {
  ModalidadSchema,
  MotivoFinalizacionPorAlumnoSchema,
  TipoInterfazSchema,
} from './common.js';
import { EstadoAsignacionSchema, EstadoSimulacionSchema } from './estados.js';

/**
 * Casing por endpoint (decisión D-1 de la tanda 1): cada endpoint tiene
 * **una** forma, la real. `GET /api/mi-simulacion`, el body de `iniciar` y
 * el de `mi-asignacion/resetear` son camelCase (verificado contra MVP el
 * 2026-09-19), aunque JC §0.1 diga "snake_case sin excepciones": hueco del
 * contrato, anotado para el core. El bloque `audio` del Paso 2, la
 * respuesta de `iniciar` y todo `finalizar` son snake_case.
 */

// ---------- GET /api/mi-simulacion (Paso 2) ----------
//
// **No es de solo lectura** (nota de interfaz §7): crea la simulación si no
// existe y marca `ABANDONADA` la que lleve más de 15 min inactiva. Se
// llama una vez al entrar, nunca en un temporizador.

export const AvatarResumenSchema = z.object({
  id: z.string(),
  nombre: z.string(),
  generoAparente: z.string(),
  rangoEtario: z.string(),
});
export type AvatarResumen = z.infer<typeof AvatarResumenSchema>;

export const EscenarioResumenSchema = z.object({
  id: z.string(),
  nombre: z.string(),
  duracionMaxMin: z.number(),
  incluyeEntrevista: z.boolean(),
  /** R3 — siempre `false` en clientes 2D; si llega `true`, el cliente lo ignora. */
  incluyeExamenFisico: z.boolean(),
  incluyeExamenesComplementarios: z.boolean(),
  /** `""` cuando el escenario no define texto; el cliente no renderiza la sección. */
  introduccionTexto: z.string(),
  introduccionUrl: z.string().nullable(),
  /**
   * `string | null`. **Asimétrico** respecto a `introduccionTexto` y
   * `cierreTexto`: sin transición el backend envía `null`, no `""`. El
   * cliente trata `null` y `""` como "no hay transición".
   */
  transicionTexto: z.string().nullable(),
  transicionUrl: z.string().nullable(),
  cierreTexto: z.string(),
  cierreUrl: z.string().nullable(),
  avatar: AvatarResumenSchema,
});
export type EscenarioResumen = z.infer<typeof EscenarioResumenSchema>;

export const MiSimulacionSimulacionSchema = z.object({
  id: z.string(),
  estado: EstadoSimulacionSchema,
  /** MAYÚSCULAS en el REST (`TEXT_TO_SPEECH`). Ver `ModalidadSchema`. */
  modalidad: ModalidadSchema,
  /** Claim del JWT: `PCVoiceClient` para este cliente. */
  tipoInterfaz: TipoInterfazSchema,
  /** R3 — siempre `false` en clientes 2D. */
  examenFisicoHabilitado: z.boolean(),
});
export type MiSimulacionSimulacion = z.infer<typeof MiSimulacionSimulacionSchema>;

export const MiAsignacionSchema = z.object({
  id: z.string(),
  estado: EstadoAsignacionSchema,
  /** ISO datetime. */
  disponibleHasta: z.string(),
});
export type MiAsignacion = z.infer<typeof MiAsignacionSchema>;

/**
 * Un sentido del bloque `audio` (JC §0.2.1). `mime_type` es el campo
 * **autoritativo**: se ramifica por él. `formato` es descriptivo y sin
 * enum. `canales` y `bits_por_muestra` son `null` en formatos comprimidos.
 */
export const AudioDeclaradoSentidoSchema = z.object({
  mime_type: z.string(),
  sample_rate: z.number(),
  canales: z.number().nullable(),
  bits_por_muestra: z.number().nullable(),
  codificacion: z.string(),
  formato: z.string(),
});
export type AudioDeclaradoSentido = z.infer<typeof AudioDeclaradoSentidoSchema>;

/**
 * Bloque `audio` del Paso 2 (JC §0.2.1). El servidor **declara** qué va a
 * enviar (`downlink`) y qué espera recibir (`uplink`); no negocia. Si el
 * cliente no puede cumplirlo, aborta **antes** de pedir el ticket (ver
 * `verificarAudioDeclarado` en `@alter/api-client`). Snake_case aunque el
 * resto del endpoint sea camelCase.
 */
export const AudioDeclaradoSchema = z.object({
  downlink: AudioDeclaradoSentidoSchema,
  uplink: AudioDeclaradoSentidoSchema,
});
export type AudioDeclarado = z.infer<typeof AudioDeclaradoSchema>;

export const MiSimulacionResponseSchema = z.object({
  simulacion: MiSimulacionSimulacionSchema,
  escenario: EscenarioResumenSchema,
  asignacion: MiAsignacionSchema,
  audio: AudioDeclaradoSchema,
  /** `true` si hay sesión `EN_CURSO` recuperable; el cliente reconecta sin nuevo ticket. */
  reconexion: z.boolean(),
});
export type MiSimulacionResponse = z.infer<typeof MiSimulacionResponseSchema>;

// ---------- POST /api/simulacion/iniciar (Paso 3; respuesta del Paso 5, JC §1) ----------

/**
 * Body camelCase: exactamente uno de los dos IDs. `z.union` de shapes
 * `.strict()` para rechazar `{}` y `{ simulacionId, asignacionId }`.
 */
export const IniciarSimulacionRequestSchema = z.union([
  z.object({ simulacionId: z.string() }).strict(),
  z.object({ asignacionId: z.string() }).strict(),
]);
export type IniciarSimulacionRequest = z.infer<typeof IniciarSimulacionRequestSchema>;

const MediaTextoAudioSchema = z.object({
  texto: z.string(),
  audio_url: z.string().nullable(),
});

/**
 * Ítem de `examenes_complementarios[]` cuando el escenario los incluye.
 * `tipo` laxo por posible crecimiento futuro ("laboratorio" | "imagenologia" hoy).
 */
export const ExamenComplementarioSchema = z.object({
  tipo: z.string(),
  nombre: z.string(),
  archivo_url: z.string(),
});
export type ExamenComplementario = z.infer<typeof ExamenComplementarioSchema>;

export const IniciarSimulacionResponseSchema = z.object({
  /** One-time, TTL 5 min. Se quema en el primer `conectar` exitoso (JC §0.3). */
  ticket: z.string(),
  simulacion_id: z.string(),
  nombre_simulacion: z.string(),
  /** "estatico" hoy. Laxo: el backend contempla fases futuras. */
  tipo_simulacion: z.string(),
  /**
   * MAYÚSCULAS en la respuesta real (`TEXT_TO_SPEECH`) aunque JC §1 muestre
   * `text_to_speech`. El cliente la devuelve en `conectar` pasando por
   * `modalidadParaConectar` (`@alter/api-client`).
   */
  modalidad: ModalidadSchema,
  introduccion: MediaTextoAudioSchema,
  /** UUID del avatar (no un nombre de carpeta). */
  avatar_id: z.string(),
  /** Ausente o vacío en escenarios sin ellos. */
  examenes_complementarios: z.array(ExamenComplementarioSchema).optional(),
  cierre: MediaTextoAudioSchema,
  /**
   * R3 — ignorado en clientes 2D. Tipado como `unknown` para no propagar
   * datos sin validar. Ausente en escenarios 2D típicos.
   */
  examen_fisico: z.unknown().optional(),
});
export type IniciarSimulacionResponse = z.infer<typeof IniciarSimulacionResponseSchema>;

// ---------- POST /api/simulacion/finalizar (JC §8) ----------

/**
 * Cierre de **negocio**. Distinto del cierre técnico del socket
 * (`sesion.finalizada_por_alumno`); los dos son obligatorios. `simulacion_id`
 * en snake_case: `simulacionId` responde 400. `resultado_examen_fisico` no
 * se declara: el examen físico está fuera del alcance de este cliente.
 */
export const FinalizarSimulacionRequestSchema = z.object({
  simulacion_id: z.string(),
  motivo: MotivoFinalizacionPorAlumnoSchema,
});
export type FinalizarSimulacionRequest = z.infer<typeof FinalizarSimulacionRequestSchema>;

/**
 * `ya_finalizada: true` significa que la simulación ya estaba cerrada y
 * este llamado no cambió nada. **Es éxito**, no error: el endpoint es
 * idempotente.
 */
export const FinalizarSimulacionResponseSchema = z.object({
  ok: z.literal(true),
  ya_finalizada: z.boolean().optional(),
});
export type FinalizarSimulacionResponse = z.infer<typeof FinalizarSimulacionResponseSchema>;

// ---------- POST /api/mi-asignacion/resetear ----------
//
// Body camelCase. Exige `es_prueba` en la asignación; `mi-simulacion` no
// expone ese dato (nota de interfaz §7), así que el cliente no puede decidir
// cuándo ofrecerlo.

export const ResetearMiAsignacionRequestSchema = z.object({
  asignacionId: z.string(),
});
export type ResetearMiAsignacionRequest = z.infer<typeof ResetearMiAsignacionRequestSchema>;
